import { useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Pen, Plus, Trash } from 'lucide-react';
import { useFormContext } from 'react-hook-form';

import SelectControl from '@/components/form/select-control';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { WithStateQuery } from '@/components/ui/with-state';
import HookFormProvider from '@/providers/hook-form-providers';
import { masterQueries } from '@/queries/master/master.queries';
import {
	vendorMutations,
	vendorQueries
} from '@/queries/vendor/vendor.queries';
import { useDialogStore } from '@/store';
import {
	segmentationDataSchema,
	type SegmentationDataSchema
} from '@/types/form/account-manager/form';
import type { DataTableResponse } from '@/types/response';
import type { VendorSegmentationDataResponse } from '@/types/vendor/vendor';

const MAX_SEGMENTATION_DATA = 10;

function Delete({ id }: Readonly<{ id: number }>) {
	const setDialog = useDialogStore((s) => s.setStore);
	const setAction = useDialogStore((s) => s.setAction);
	const onOpen = useDialogStore((s) => s.onOpenChange);

	const action = useMutation(vendorMutations.segmentationDataDelete(id));

	return (
		<Button
			variant={'outline'}
			size={'icon'}
			className="border-destructive text-destructive hover:text-destructive/50 hover:border-destructive/70 w-20 border-2 text-sm"
			onClick={() => {
				setDialog('variant', 'warning');
				setDialog('confirmText', 'Ya, Hapus');
				setDialog('title', 'Hapus Data?');
				setDialog(
					'description',
					'Apakah Anda yakin ingin menghapus data segmentasi?'
				);
				setDialog('cancelText', 'Batal');
				setAction('onCancel', () => onOpen(false));
				setAction('onConfirm', () =>
					action.mutate(undefined, {
						onSuccess: () => {
							setDialog('variant', 'success');
							setDialog('confirmText', 'OK');
							setDialog('title', 'Berhasil');
							setDialog('description', 'Data Segmentasi Berhasil Dihapus');
							setAction('onConfirm', () => onOpen(false));
							onOpen(true);
						},
						onError: ({ message }) => {
							setDialog('variant', 'error');
							setDialog('title', 'Gagal');
							setDialog('description', message);
							setDialog('confirmText', 'OK');
							onOpen(true);
							setAction('onConfirm', () => onOpen(false));
						}
					})
				);
				onOpen(true);
			}}
		>
			<Trash />
			Hapus
		</Button>
	);
}

const columns = ({
	handleUpdate,
	isDisabled
}: {
	handleUpdate: (data: SegmentationDataSchema, id: number) => void,
	isDisabled?: boolean
}): ColumnDef<VendorSegmentationDataResponse>[] => [
	{
		accessorFn: (_, i) => i.toString(),
		accessorKey: 'no',
		header: 'No',
		cell: ({ row, table: { getState } }) =>
			row.index +
			1 +
			getState().pagination.pageIndex * getState().pagination.pageSize,
		meta: { className: 'w-4 text-center', id: 'no' }
	},
	{
		accessorFn: (r) => r.businessFieldName,
		accessorKey: 'businessFieldName',
		header: 'Bidang Usaha',
		cell: ({ row: { original } }) => original.businessFieldName,
		meta: { id: 'businessFieldName' }
	},
	{
		accessorFn: (r) => r.subBusinessFieldName,
		accessorKey: 'subBusinessFieldName',
		header: 'Sub Bidang Usaha',
		cell: ({ row: { original } }) => original.subBusinessFieldName,
		meta: { id: 'subBusinessFieldName' }
	},
	{
		accessorFn: () => 'actions',
		accessorKey: 'actions',
		header: 'Aksi',
		cell: ({ row: { original } }) => (
			<div className="flex gap-2 py-2">
				<Button
					variant={'outline'}
					size={'icon'}
					className="w-20 border-2 text-sm"
					onClick={() =>
						handleUpdate(
							{
								businessFieldId: original.businessFieldId,
								subBusinessFieldId: original.subBusinessFieldId
							},
							original.id
						)
					}
				>
					<Pen />
					Ubah
				</Button>
				<Delete id={original.id} />
			</div>
		),
		minSize: 70,
		maxSize: 70,
		meta: {
			id: 'actions',
			isVisible: !isDisabled,
			className: 'w-28'
		}
	}
];

function BusinessField() {
	const { setValue } = useFormContext<SegmentationDataSchema>();
	const options = useQuery(masterQueries.listBusinessField({ showAll: true }));

	return (
		<SelectControl
			name="businessFieldId"
			options={
				options.data
					? options.data.data.map((v) => ({
							...v,
							label: v.name,
							value: v.id
						}))
					: []
			}
			label="Bidang Usaha"
			isLoading={options.isPending}
			isSearchable
			setter={() => {
				setValue('subBusinessFieldId', null, { shouldDirty: true });
			}}
		/>
	);
}

function SubBusinessField() {
	const { watch } = useFormContext<SegmentationDataSchema>();
	const [businessFieldId] = watch(['businessFieldId']);

	const options = useQuery(
		masterQueries.listBusinessField({ keyword: businessFieldId })
	);

	const opts = options.data?.data.find((v) => v.id === businessFieldId);
	return (
		<SelectControl
			name="subBusinessFieldId"
			options={
				options.data && !!opts
					? opts.subBusinessFields.map((v) => ({
							...v,
							label: v.name,
							value: v.id
						}))
					: []
			}
			label="Sub Bidang Usaha"
			isLoading={options.isPending}
			isSearchable
		/>
	);
}

function FormSegmentationData({
	businessFieldId = null,
	subBusinessFieldId = null,
	id,
	handleCloseDialog
}: Readonly<
	SegmentationDataSchema & { id?: number, handleCloseDialog: () => void }
>) {
	const setDialog = useDialogStore((s) => s.setStore);
	const setAction = useDialogStore((s) => s.setAction);
	const onOpen = useDialogStore((s) => s.onOpenChange);
	const create = useMutation(vendorMutations.segmentationData);
	const update = useMutation(vendorMutations.segmentationDataUpdate(id));

	function onSuccess({ message }: { message: string }) {
		setDialog('variant', 'success');
		setDialog('title', 'Berhasil');
		setDialog('description', message);
		setDialog('confirmText', 'OK');
		setAction('onConfirm', () => {
			onOpen(false);
			handleCloseDialog();
		});
		setDialog('open', true);
	}
	function onError({ message }: { message: string }) {
		setDialog('variant', 'error');
		setDialog('title', 'Gagal');
		setDialog('description', message);
		setDialog('confirmText', 'OK');
		setAction('onConfirm', () => onOpen(false));
		setDialog('open', true);
	}

	const handleSubmit = (data: SegmentationDataSchema) => {
		if (id) {
			update.mutate(data, {
				onSuccess: () => onSuccess({ message: 'Data Segmentasi Berhasil Diubah' }),
				onError: ({ message }) => onError({ message })
			});

			return;
		}

		create.mutate(data, {
			onSuccess: () =>
				onSuccess({ message: 'Data Segmentasi Berhasil Ditambahkan' }),
			onError: ({ message }) => onError({ message })
		});
	};

	return (
		<HookFormProvider
			schema={segmentationDataSchema}
			defaultValues={{
				businessFieldId,
				subBusinessFieldId
			}}
			onSubmit={handleSubmit}
			className="my-2"
			formOptions={{ mode: 'onTouched' }}
		>
			<div className="grid w-full grid-cols-1 gap-x-4 gap-y-2">
				<BusinessField />
				<SubBusinessField />
			</div>
			<div className="mt-2 flex w-full justify-end">
				<Button>Simpan</Button>
			</div>
		</HookFormProvider>
	);
}

export default function FormSegmentation({
	isDisabled = false,
	segmentations
}: Readonly<{
	isDisabled?: boolean,
	segmentations?: DataTableResponse<VendorSegmentationDataResponse>
}>) {
	const [visible, setVisible] = useState<{
		open: boolean,
		data: SegmentationDataSchema,
		id?: number
	}>({
		open: false,
		data: {
			businessFieldId: null,
			subBusinessFieldId: null
		}
	});

	const segmentationData = useQuery({
		...vendorQueries.segmentationData(),
		enabled: !segmentations
	});

	const handleOpenDialog = (data: SegmentationDataSchema, id?: number) => {
		setVisible({ open: true, id, data });
	};

	const handleCloseDialog = () =>
		setVisible((prev) => ({ ...prev, open: false }));

	return (
		<>
			<div className="my-2 flex flex-col gap-2">
				<div className="flex items-center justify-between">
					<p className="col-span-full text-xl font-medium">Informasi Segmentasi</p>
					<WithStateQuery state={segmentationData}>
						{({ count }) =>
							count < MAX_SEGMENTATION_DATA && !isDisabled ? (
								<Button
									type="button"
									onClick={() =>
										handleOpenDialog({
											businessFieldId: null,
											subBusinessFieldId: null
										})
									}
								>
									<Plus />
									Tambah Data
								</Button>
							) : null
						}
					</WithStateQuery>
				</div>

				<DataTable
					columns={columns({ handleUpdate: handleOpenDialog, isDisabled })}
					data={segmentations?.data ?? segmentationData.data?.data}
					isLoading={segmentations ? false : segmentationData.isPending}
					total={segmentations ? segmentations.count : segmentationData.data?.count}
					isUsePagination={false}
				/>
			</div>
			<Dialog
				open={visible.open}
				onOpenChange={() =>
					setVisible((prev) => ({
						...prev,
						open: false
					}))
				}
			>
				<DialogContent>
					<DialogTitle className="flex flex-col">
						<span className="text-lg text-black">Informasi Segmentasi</span>
						<span className="text-label-input">
							Silahkan Isi Data Segmentasi Anda
						</span>
					</DialogTitle>
					<FormSegmentationData
						{...visible.data}
						id={visible.id}
						handleCloseDialog={handleCloseDialog}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
}
